import { useNotificationStore } from '@knocklabs/react-core'
import { Assessment } from '@seen/core/models/assessments/assessment'

import { cn, KBD, TypographyDetail, TypographySubtle } from '@seen/ui'
import {
  IconAlignBoxLeftTop,
  IconCalendar,
  IconCheck,
  IconClipboard,
  IconDesk,
  IconDots,
  IconFile,
  IconInbox,
  IconPhone,
  IconStethoscope,
  IconUser,
} from '@tabler/icons-react'
import type { JSX, ReactNode } from 'react'
import { useEffect } from 'react'
import { useMatch } from 'react-router'
import { FeedbackButton } from '#src/components/app/FeedbackButton.tsx'
import { NavigationMenu } from '#src/components/core/navigation-menu/NavigationMenu.tsx'
import { NavigationMenuLink } from '#src/components/core/navigation-menu/NavigationMenuLink.tsx'
import { NavigationMenuList } from '#src/components/core/navigation-menu/NavigationMenuList.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconCarePlan } from '#src/components/icons/IconCarePlan.tsx'
import { KnockOrbitSidebarBanner } from '#src/components/knock/KnockOrbitSidebarBanner.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import {
  type PractitionerPerms,
  useCurrentPractitionerPerms,
} from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useKnockFeedClient } from '#src/hooks/useKnockFeedClient.ts'
import { KNOCK_PAGE_SIZE } from '#src/hooks/useNotificationItems.ts'
import { useParticipantNoteDrafts } from '#src/hooks/useParticipantNoteDrafts.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface AppNavigationMenuProps {
  className?: string
}

interface NavItem {
  label: string
  to: string
  icon: (...args: any[]) => ReactNode
  external?: boolean
  shortcut?: {
    first: string
    second: string
  }
}

const INBOX_ITEM: NavItem = {
  label: 'Inbox',
  to: '/',
  icon: IconInbox,
  shortcut: { first: 'G', second: 'I' },
}

const DRAFTS_ITEM: NavItem = {
  label: 'Drafts',
  to: '/drafts',
  icon: IconFile,
}

const PARTICIPANTS_ITEM: NavItem = {
  label: 'Participants',
  to: '/Patient',
  icon: IconUser,
}

const CALENDAR_ITEM: NavItem = {
  label: 'Calendar',
  to: '/calendar',
  icon: IconCalendar,
  shortcut: { first: 'G', second: 'C' },
}

const TASKS_ITEM: NavItem = {
  label: 'Tasks',
  to: '/tasks',
  icon: IconCheck,
}

const SDR_ITEM: NavItem = {
  label: 'SDRs',
  to: '/sdr',
  icon: IconClipboard,
}

const ORDERS_ITEM: NavItem = {
  label: 'Orders',
  to: '/orders',
  icon: IconStethoscope,
}

const PHONE_CALLS_ITEM: NavItem = {
  label: 'Calls',
  to: '/calls',
  icon: IconPhone,
}

const DESKS_ITEM: NavItem = {
  label: 'Desks',
  to: '/desks',
  icon: IconDesk,
}

const IDT_LOG_DESK_URL = '/desks/a4324a62-0819-424f-85db-e6e590ab8476'
const IDT_CARE_PLANNING_DESK_URL = '/desks/0196874f-ae24-741d-9098-6f4083463fe8'

const IDT_LOG_ITEM: NavItem = {
  label: 'IDT Logs',
  to: IDT_LOG_DESK_URL,
  icon: IconAlignBoxLeftTop,
}

const IDT_CARE_PLANNING_ITEM: NavItem = {
  label: 'IDT Care Planning',
  to: IDT_CARE_PLANNING_DESK_URL,
  icon: IconAlignBoxLeftTop,
}

const CARE_PLANS_ITEM: NavItem = {
  label: 'Care Plans',
  to: '/care-plans',
  icon: IconCarePlan,
}

const MORE_ITEM: NavItem = {
  label: 'More',
  to: '/more',
  icon: IconDots,
}

export function getAppNavigationItems(perms: PractitionerPerms): NavItem[] {
  if (perms.isAuditor) {
    return [PARTICIPANTS_ITEM, SDR_ITEM, IDT_LOG_ITEM, IDT_CARE_PLANNING_ITEM]
  }

  if (perms.isTech || perms.isIDT) {
    return [
      INBOX_ITEM,
      DRAFTS_ITEM,
      PARTICIPANTS_ITEM,
      CALENDAR_ITEM,
      TASKS_ITEM,
      SDR_ITEM,
      ORDERS_ITEM,
      PHONE_CALLS_ITEM,
      CARE_PLANS_ITEM,
      DESKS_ITEM,
      IDT_LOG_ITEM,
      MORE_ITEM,
    ]
  }

  if (perms.isMOE) {
    return [
      INBOX_ITEM,
      DRAFTS_ITEM,
      CALENDAR_ITEM,
      TASKS_ITEM,
      DESKS_ITEM,
      MORE_ITEM,
    ]
  }

  return []
}

export function getFirstVisibleNavPath(
  perms: PractitionerPerms
): string | undefined {
  return getAppNavigationItems(perms).find((item) => !item.external)?.to
}

export function AppNavigationMenu({
  className,
}: AppNavigationMenuProps): JSX.Element {
  const isInbox = useMatch('/inbox/*')
  const isIdtLog = !!useMatch(IDT_LOG_DESK_URL)
  const perms = useCurrentPractitionerPerms()

  const APP_NAV_ITEMS = getAppNavigationItems(perms)

  return (
    <div className={cn('flex min-w-40 flex-col justify-between', className)}>
      <ScrollArea scrollRestorationId={null}>
        <NavigationMenu className="flex max-w-none flex-col items-stretch justify-start px-2">
          <NavigationMenuList className="flex flex-col items-stretch gap-0.5">
            {APP_NAV_ITEMS.map(
              ({ to, icon: Icon, label, shortcut, external }) => (
                <Tooltip key={to}>
                  <TooltipTrigger asChild>
                    <NavigationMenuLink
                      to={to}
                      variant="ghost"
                      target={external ? '_blank' : undefined}
                      rel={external ? 'noopener noreferrer' : undefined}
                      className={cn(
                        'group/item justify-between py-2 pr-2.5 pl-2 hover:bg-frame-hover',
                        (to !== DESKS_ITEM.to ||
                          (to === DESKS_ITEM.to && !isIdtLog)) &&
                          '[&.active]:bg-frame-active',
                        to === INBOX_ITEM.to && !!isInbox && 'active'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="size-4 group-hover/item:text-contrast" />
                        <TypographySubtle className="font-medium">
                          {label}
                        </TypographySubtle>
                      </div>
                      {to === INBOX_ITEM.to && <InboxUnreadIndicator />}
                      {to === DRAFTS_ITEM.to && <DraftsUnreadIndicator />}
                    </NavigationMenuLink>
                  </TooltipTrigger>
                  {shortcut && (
                    <TooltipContent
                      side="right"
                      className="flex items-center gap-2 px-2"
                    >
                      <TypographyDetail className="text-muted-foreground">
                        {label}
                      </TypographyDetail>
                      <div className="flex items-center gap-1">
                        <KBD>{shortcut.first}</KBD>
                        <TypographyDetail className="text-muted-foreground">
                          then
                        </TypographyDetail>
                        <KBD>{shortcut.second}</KBD>
                      </div>
                    </TooltipContent>
                  )}
                </Tooltip>
              )
            )}
          </NavigationMenuList>
        </NavigationMenu>
      </ScrollArea>
      {/* w-0 + min-w-full lets the announcement/feedback slot fill the sidebar's
          width without contributing its cards' max-content width back to it, so a
          wide banner can never widen the sidebar. */}
      <div className="flex w-0 min-w-full flex-col gap-2 bg-frame px-2">
        <KnockOrbitSidebarBanner />
        <FeedbackButton />
      </div>
    </div>
  )
}

function InboxUnreadIndicator(): ReactNode {
  const feedClient = useKnockFeedClient()
  const { metadata } = useNotificationStore(feedClient)

  useEffect(() => {
    void feedClient.fetch({ page_size: KNOCK_PAGE_SIZE })
  }, [feedClient])

  if (metadata.unread_count === 0) {
    return null
  }
  return (
    <TypographyDetail className="text-frame-foreground-secondary">
      {metadata.unread_count}
    </TypographyDetail>
  )
}

function DraftsUnreadIndicator(): ReactNode {
  const practitioner = useCurrentPractitioner()
  const { data: assessments } = useSearch(Assessment, {
    ...Assessment.searchQuery({ practitioner: practitioner.reference }),
    'attester:missing': true,
  })

  const { data: participantNotes } = useParticipantNoteDrafts(
    practitioner.reference
  )

  const numDrafts = (assessments?.length ?? 0) + (participantNotes?.length ?? 0)
  if (numDrafts === 0) {
    return null
  }
  return (
    <TypographyDetail className="text-frame-foreground-secondary">
      {numDrafts}
    </TypographyDetail>
  )
}
